import { Contacts_Contact as CustomerContact } from "gen/customer/models_pb"
import { property } from "lodash"
import { ReactNode, useMemo } from "react"
import { formatPhone } from "shared-ts/utils"

import useLocalSearchPopoverMultiSelect, {
  UseLocalSearchPopoverMultiSelectProps,
  UseLocalSearchPopoverSelectRes,
} from "./useLocalSearchPopoverMultiSelect"

type UseCustomerContactsPopoverMultiSelectProps = Omit<
  UseLocalSearchPopoverMultiSelectProps<CustomerContact>,
  | "items"
  | "keyExtractor"
  | "labelExtractor"
  | "contentExtractor"
  | "defaultSelected"
> & {
  contacts: CustomerContact[]
  defaultSelectedIdStrings: string[]
}

function useCustomerContactsPopoverMultiSelect({
  contacts,
  defaultSelectedIdStrings,
  ...rest
}: UseCustomerContactsPopoverMultiSelectProps): UseLocalSearchPopoverSelectRes<CustomerContact> {
  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => a.name.localeCompare(b.name))
  }, [contacts])

  const defaultSelected = useMemo(() => {
    return defaultSelectedIdStrings
      .map((id) => sortedContacts.find((c) => c.idString === id))
      .filter((c): c is CustomerContact => !!c)
  }, [defaultSelectedIdStrings, sortedContacts])

  return useLocalSearchPopoverMultiSelect({
    items: sortedContacts,
    keyExtractor,
    labelExtractor,
    contentExtractor,
    defaultSelected,
    ...rest,
  })
}

const keyExtractor: (contact: CustomerContact) => string = property("idString")
const labelExtractor: (contact: CustomerContact) => string = property("name")

function contentExtractor(contact: CustomerContact): ReactNode {
  const phone = formatPhone(contact.phone)
  return (
    <span className="flex flex-col">
      <span>{contact.name}</span>
      {(phone || contact.email) && (
        <span className="color-subtle text-body">
          {phone}
          {phone && contact.email && (
            <span className="text-v2-foreground-default-weak mx-1">•</span>
          )}
          {contact.email}
        </span>
      )}
    </span>
  )
}

export default useCustomerContactsPopoverMultiSelect
